import { create } from "zustand";
import { useProjectStore } from "@/zapi-frontend/stores/project";

export type TVersionsInlineMode = "new" | "rename" | "delete";

export type TVersionsInline = {
  rowId: string;
  mode: TVersionsInlineMode;
  value: string;
  sourceId: string | null;
  targetId: string | null;
  pending: boolean;
};

type TStore = {
  openPopoverVersionGroupId: string | null;
  setOpenPopoverVersionGroupId: (id: string | null) => void;
  inline: TVersionsInline | null;
  setInline: (next: TVersionsInline | null) => void;
  patchInline: (patch: Partial<TVersionsInline>) => void;
  optimisticRename: (rowProjectId: string, versionId: string, newName: string) => void;
  optimisticStartFirst: (rowProjectId: string, name: string) => void;
  optimisticAdd: (rowProjectId: string, name: string) => void;
  optimisticRemove: (rowProjectId: string, versionId: string) => void;
};

export const useProjectVersionsStore = create<TStore>((set) => ({
  openPopoverVersionGroupId: null,
  setOpenPopoverVersionGroupId: (id) => set({ openPopoverVersionGroupId: id }),
  inline: null,
  setInline: (next) => set({ inline: next }),
  patchInline: (patch) => set((s) => ({ inline: s.inline ? { ...s.inline, ...patch } : null })),

  optimisticRename: (rowProjectId, versionId, newName) => {
    const { projects, setProjects } = useProjectStore.getState();
    setProjects(
      projects.map((p) =>
        p.id === rowProjectId
          ? { ...p, versions: p.versions?.map((v) => (v.id === versionId ? { ...v, name: newName } : v)) }
          : p
      )
    );
  },

  optimisticStartFirst: (rowProjectId, name) => {
    const { projects, setProjects } = useProjectStore.getState();
    const now = new Date().toISOString();
    setProjects(
      projects.map((p) => {
        if (p.id !== rowProjectId) return p;
        const initialDate = p.versions?.[0]?.date ?? now;
        const initialName = p.versions?.[0]?.name ?? "Initial";
        return {
          ...p,
          versionGroupId: p.versionGroupId ?? `optimistic-${rowProjectId}`,
          versionGroupNewest: true,
          versions: [
            { id: `optimistic-${Date.now()}`, name, date: now },
            { id: rowProjectId, name: initialName, date: initialDate }
          ]
        };
      })
    );
  },

  optimisticAdd: (rowProjectId, name) => {
    const { projects, setProjects } = useProjectStore.getState();
    const now = new Date().toISOString();
    setProjects(
      projects.map((p) =>
        p.id === rowProjectId
          ? { ...p, versions: [{ id: `optimistic-${Date.now()}`, name, date: now }, ...(p.versions ?? [])] }
          : p
      )
    );
  },

  optimisticRemove: (rowProjectId, versionId) => {
    const { projects, setProjects } = useProjectStore.getState();
    setProjects(
      projects.map((p) => {
        if (p.id !== rowProjectId) return p;
        const remaining = (p.versions ?? []).filter((v) => v.id !== versionId);
        if (remaining.length === 0) return { ...p, versionGroupId: null, versions: [] };
        return { ...p, versions: remaining };
      })
    );
  }
}));
